import { MockGooglePlace, generateMockPlaces } from './mockGoogleMapsApi';
import { isGoogleMapsApiLoaded } from './mapsScriptLoader';

// Helper to ensure PlacesService is available
const getPlacesService = (): any | null => { // Return any to bypass specific type issues
  if (isGoogleMapsApiLoaded() && window.google && window.google.maps && window.google.maps.places) {
    // Use type assertion if PlacesService is not recognized
    return new (window.google.maps.places as any).PlacesService(document.createElement('div'));
  }
  console.error('Google Maps JavaScript API with Places library is not loaded.');
  return null;
};

// Helper to ensure AutocompleteService is available
const getAutocompleteService = (): any | null => { // Return any
  if (isGoogleMapsApiLoaded() && window.google && window.google.maps && window.google.maps.places) {
    // Use type assertion if AutocompleteService is not recognized
    return new (window.google.maps.places as any).AutocompleteService();
  }
  console.error('Google Maps JavaScript API with Places library is not loaded for Autocomplete.');
  return null;
};

export const searchGooglePlaces = (query: string): Promise<MockGooglePlace[]> => {
  return new Promise((resolve) => {
    console.log('Searching for business predictions with query (JS API - Autocomplete):', query);
    const service = getAutocompleteService();

    if (!service) {
      console.warn('Google Maps API (Autocomplete) not available, falling back to mock data for search.');
      resolve(generateMockPlaces(query));
      return;
    }

    const request = {
      input: query,
      types: ['establishment'],
    } as any; // Use 'as any' for AutocompletionRequest

    service.getPlacePredictions(request, (predictions: any[] | null, status: any) => { // Use 'any' for predictions and status
      if (status === (window.google.maps.places as any).PlacesServiceStatus.OK && predictions) {
        resolve(predictions.map(prediction => ({
          place_id: prediction.place_id || `mock-id-${Math.random()}`,
          name: (prediction.structured_formatting as any)?.main_text || prediction.description || 'Unnamed Place',
          formatted_address: prediction.description || '',
          vicinity: (prediction.structured_formatting as any)?.secondary_text || prediction.description?.split(',')[0] || '',
          geometry: undefined, 
          types: prediction.types || []
        } as MockGooglePlace)));
      } else if (status === (window.google.maps.places as any).PlacesServiceStatus.ZERO_RESULTS) {
        resolve([]);
      } else {
        console.error('Google Places getPlacePredictions failed with status:', status);
        console.warn('Google Places API autocomplete error, falling back to mock data.');
        resolve(generateMockPlaces(query));
      }
    });
  });
};

export const getPlaceDetails = (placeId: string): Promise<any> => { // Return type 'any' for now
  return new Promise((resolve, reject) => {
    if (!placeId) {
      reject(new Error('Place ID is required'));
      return;
    }

    // Handle mock place IDs if still needed for testing
    if (placeId.startsWith('mock-id-')) {
      console.warn(`Using mock details for mock place ID: ${placeId}`);
      const mockDetails = {
        name: `Mock Business for ${placeId}`,
        formatted_phone_number: '(555) 123-4567',
        website: 'https://example.com',
        formatted_address: '123 Mock St, Mockville, MC 00000',
        address_components: [
          { long_name: '123', short_name: '123', types: ['street_number'] },
          { long_name: 'Mock Street', short_name: 'Mock St', types: ['route'] },
          { long_name: 'Mockville', short_name: 'Mockville', types: ['locality'] },
          { long_name: 'Mock County', short_name: 'MC', types: ['administrative_area_level_1'] },
          { long_name: '00000', short_name: '00000', types: ['postal_code'] },
        ],
        geometry: { location: { lat: () => 37.422, lng: () => -122.084 } },
        // Add other fields as needed by the consuming code
      };
      resolve(mockDetails);
      return;
    }
    
    const service = getPlacesService();
    if (!service) {
      reject(new Error('Google Maps API not available for place details.'));
      return;
    }

    const request = { // Define request as an object
      placeId: placeId,
      fields: [
        'name', 'formatted_address', 'formatted_phone_number', 'website', 
        'address_components', 'geometry.location', 'opening_hours',
        'business_status', 'type', 'vicinity', 'place_id'
      ]
    } as any; // Use 'as any' for PlaceDetailsRequest

    service.getDetails(request, (place: any | null, status: any) => { // Use 'any' for place and status
      if (status === (window.google.maps.places as any).PlacesServiceStatus.OK && place) {
        let street = '';
        let city = '';
        let state = '';
        let zip = '';
        
        if (place.address_components) {
          for (const component of place.address_components) {
            const types = component.types || [];
            if (types.includes('street_number')) street = component.long_name + ' ';
            else if (types.includes('route')) street += component.long_name;
            else if (types.includes('locality')) city = component.long_name;
            else if (types.includes('administrative_area_level_1')) state = component.short_name;
            else if (types.includes('postal_code')) zip = component.long_name;
          }
        }
        
        let founded = '';
        // Simplified 'founded' logic, as opening_hours.periods[0].open.date is not standard
        // The original logic for 'founded' was highly speculative.
        // Consider removing or making it simpler, or relying on user input.

        resolve({
          name: place.name,
          phone: place.formatted_phone_number || '',
          email: '', // Email is not provided by Places API
          address: street.trim() || place.formatted_address?.split(',')[0] || '',
          city,
          state,
          zip,
          website: place.website || '',
          founded, // This will be empty unless specific logic is added
          // opening_date: founded, // Redundant if founded is the same
          industry: '', // Not directly available, usually needs mapping from place.types
          coordinates: place.geometry?.location ? {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          } : undefined,
          place_id: place.place_id,
          // Include other relevant fields from 'place' object if needed
        });
      } else {
        console.error('Google Places getDetails failed with status:', status);
        reject(new Error(`Google Places getDetails failed: ${status}`));
      }
    });
  });
};
